import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailsHeader } from '@/components/ui/entity-details-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import type { LimitViewModel } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { FileText, Gauge, History, Link2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LimitReferencesTab } from './limit-references-tab';
import { LIMIT_QUERY } from './limits.gql.tsx';

type LimitDetailsData = LimitViewModel;

interface LimitDetailsProps {
	limitId: string;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

const formatCurrency = (
	value: number | null | undefined,
	t?: (key: string) => string
) => {
	if (value == null) {
		return t ? t('details.noLimit') : 'No limit';
	}

	return new Intl.NumberFormat(undefined, {
		style: 'currency',
		currency: 'EUR'
	}).format(value);
};

const formatDuration = (
	value: number | null | undefined,
	t?: (key: string) => string
) => {
	if (value == null) {
		return t ? t('details.noLimit') : 'No limit';
	}

	const hours = Math.floor(value / 60);
	const minutes = value % 60;

	if (hours > 0 && minutes > 0) {
		return `${hours}h ${minutes}m`;
	}

	if (hours > 0) {
		return `${hours}h`;
	}

	return `${minutes}m`;
};

export function LimitDetails({
	limitId,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: LimitDetailsProps) {
	const { t } = useTranslation('common');
	const { t: tL } = useTranslation('limits');
	const [activeTab, setActiveTab] = useState('details');
	// Track if references tab has been opened (for lazy loading)
	const [hasOpenedReferences, setHasOpenedReferences] = useState(false);

	const handleTabChange = (value: string) => {
		setActiveTab(value);
		if (value === 'references' && !hasOpenedReferences) {
			setHasOpenedReferences(true);
		}
	};

	const { data, isLoading, isError } = useOne<LimitDetailsData>({
		resource: 'limits',
		id: limitId,
		meta: {
			gqlQuery: LIMIT_QUERY,
			dataPath: 'limit'
		}
	});

	// For details pages, show loading immediately to prevent flash during navigation
	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 100, // Shorter delay for details pages
		showInitialLoading: true // Show loading immediately during navigation
	});

	const limit = data?.data;

	// Show skeleton during loading
	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-48 h-6 rounded bg-muted animate-pulse" />
				<div className="w-32 h-6 rounded bg-muted animate-pulse" />
				<div className="space-y-2">
					<div className="w-full h-4 rounded bg-muted animate-pulse" />
					<div className="w-3/4 h-4 rounded bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	// Only show error when not loading
	if (!isLoading && (isError || !limit)) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				{tL('details.loadError')}
			</div>
		);
	}

	// If we don't have data yet, don't render anything (let loading states handle it)
	if (!limit) {
		return null;
	}

	const getAmountRangeDisplay = () => {
		const { minAmount, maxAmount } = limit;
		if (minAmount !== null && maxAmount !== null) {
			return `${formatCurrency(minAmount)} - ${formatCurrency(maxAmount)}`;
		}
		if (minAmount !== null) {
			return `Min: ${formatCurrency(minAmount)}`;
		}
		if (maxAmount !== null) {
			return `Max: ${formatCurrency(maxAmount)}`;
		}
		return 'No amount limits';
	};

	const getDurationRangeDisplay = () => {
		const { minStayMinutes, maxStayMinutes } = limit;
		if (minStayMinutes !== null && maxStayMinutes !== null) {
			return `${formatDuration(minStayMinutes)} - ${formatDuration(maxStayMinutes)}`;
		}
		if (minStayMinutes !== null) {
			return `Min: ${formatDuration(minStayMinutes)}`;
		}
		if (maxStayMinutes !== null) {
			return `Max: ${formatDuration(maxStayMinutes)}`;
		}
		return 'No duration limits';
	};

	const displayName = limit.name || tL('details.unnamedLimit');
	const entityTypeName = t('limit');

	return (
		<div className="flex flex-col h-full overflow-x-hidden">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<EntityDetailsHeader
					icon={
						<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
							<Gauge className="w-7 h-7 text-gray-600 dark:text-gray-300" />
						</div>
					}
					name={displayName}
					entityTypeName={entityTypeName}
					entityId={limitId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4 border-b border-gray-200 dark:border-slate-700"
				/>
			)}

		<Tabs
			value={activeTab}
			onValueChange={handleTabChange}
			className="w-full"
		>
			<TabsList className="gap-2 bg-transparent p-0">
				<TabsTrigger
					value="details"
					className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
				>
					<FileText className="w-4 h-4" />
					<span>{t('tabs.details')}</span>
				</TabsTrigger>
				<TabsTrigger
					value="references"
					className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
				>
					<Link2 className="w-4 h-4" />
					<span>{t('tabs.references')}</span>
				</TabsTrigger>
				<TabsTrigger
					value="audit"
					align="right"
					className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
				>
					<History className="w-4 h-4" />
					<span>{t('tabs.audit')}</span>
				</TabsTrigger>
			</TabsList>

			<TabsContent value="details" className="space-y-6 mt-0 pt-4">
				<DetailSection title={t('sections.amount_constraints')} noBorderTop>
					<DetailFieldsGrid>
						<DetailField
							label={tL('details.amountRange')}
							value={getAmountRangeDisplay()}
						/>

						<DetailField
							label={tL('details.minAmount')}
							value={formatCurrency(limit.minAmount, tL)}
						/>
						<DetailField
							label={tL('details.maxAmount')}
							value={formatCurrency(limit.maxAmount, tL)}
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.duration_constraints')}>
					<DetailFieldsGrid>
						<DetailField
							label={tL('details.stayRange')}
							value={getDurationRangeDisplay()}
						/>

						<DetailField
							label={tL('details.minStay')}
							value={formatDuration(limit.minStayMinutes, tL)}
						/>
						<DetailField
							label={tL('details.maxStay')}
							value={formatDuration(limit.maxStayMinutes, tL)}
						/>
					</DetailFieldsGrid>
				</DetailSection>
			</TabsContent>

			<TabsContent value="references" className="mt-6">
				{/* Only render references tab content after it has been opened (lazy loading) */}
				{hasOpenedReferences && (
					<LimitReferencesTab limitId={limitId} />
				)}
			</TabsContent>

			<TabsContent value="audit" className="mt-6">
				<AuditTab data={{}} />
			</TabsContent>
		</Tabs>
		</div>
	);
}
